import type { GraphQLResolveInfo, Source } from "graphql";
import type { ContainerInstance } from "typedi";

import type { IKazooIdToken } from "./IKazooIdToken";
import type { User } from "./User";

export interface ITracer {
  traceResolver(info: GraphQLResolveInfo, callback: Function): unknown;
}

export interface GraphQLParameters<TVariables = unknown> {
  contextValue: ContextLike;
  source: string | Source;
  variableValues?: TVariables;
}

export type PrincipalIdentifier =
  | `${"kazoo" | "yei" | "superadmin" | "robot"}:${string}`
  | `${"sub" | "id" | "none"}:${string}` // TODO: WT-19188 We log when it happens and if it doesn't happen, we should remove this
  | `${"task"}:${string}` // the intent is to deprecate this when tasks get smarter about execute on behalf of
  | "PLATFORM ADMIN"; // the intent is to deprecate this soon as we move to superadmin:oktaId everywhere

/**
 * A small stub of KazooContext provided only to prevent circular
 * dependencies.
 *
 * You will know if you need to use this. Otherwise, use KazooContext.
 */
export interface ContextLike {
  claims: IKazooIdToken | undefined;
  /**
   * @param {key} string The claim value you'd like returned.
   * @throws will throw Missing Kazoo ID token if user is not authenticated.
   */
  claim: <K extends keyof IKazooIdToken>(key: K) => IKazooIdToken[K];
  /**
   * Gets the current user.
   *
   * @deprecated for backwards compatibility only.
   */
  currentUser: () => Promise<User | null>;
  /**
   * Gets the current user.
   *
   * @throws {NotAuthenticatedError} if the current user is unavailable.
   */
  getUser: () => User;
  /**
   * Gets the current user or null if unavailable.
   */
  maybeGetUser: () => User | null;
  currentYeiHost: (path?: string) => string;
  currentKwHost: (path?: string) => string;
  /**
   * The Feature set available to the current user.
   * Represents the user's module access.
   *
   * Robots will not have a set of features here. Use `@kazoohr/settings#getFeatureSetForWorkspace` when an action may be performed by a robot.
   */
  features: Record<string, boolean>;
  /**
   * The Feature set available to the current company.
   * Represents every feature the company has enabled.
   */
  companyFeatures: Record<string, boolean>;
  requestId: string;
  services: ContainerInstance;
  t: (key: string, ...rest: any[]) => string;
  tracer: ITracer;
  wantsMocksFor: (mode: string) => boolean;
  readonly id: string;
  /**
   * The principal actor in this request. Form of `source:identifier`. E.g.,
   * if this is an HG-only user, `hg:user-hg-uuid`, if this is a robot user,
   * it's `robot:robot-identifier` (note: robot identifiers aren't UUIDs).
   */
  readonly principal: PrincipalIdentifier;
  readonly impersonator: string | undefined;
  readonly workspace: string | undefined;
}
